import React from 'react';
import { motion } from 'framer-motion';

const LoadingSpinner = ({
  size = 'md',
  color = 'blue',
  type = 'spinner',
  text,
  fullScreen = false,
  className = ''
}) => {
  const sizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };
  
  const colors = {
    blue: 'text-blue-600',
    green: 'text-emerald-600',
    red: 'text-red-600',
    purple: 'text-purple-600',
    gray: 'text-gray-600',
    white: 'text-white'
  };
  
  const spinnerVariants = {
    start: {
      rotate: 0
    },
    end: {
      rotate: 360
    }
  };
  
  const pulseVariants = {
    start: {
      opacity: 0.4,
      scale: 0.8
    },
    end: {
      opacity: 1,
      scale: 1
    }
  };
  
  const bounceVariants = {
    start: {
      y: 0
    },
    end: {
      y: -20
    }
  };
  
  const renderSpinner = () => {
    switch (type) {
      case 'spinner':
        return (
          <motion.svg
            className={`animate-spin ${sizes[size]} ${colors[color]} ${className}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            variants={spinnerVariants}
            initial="start"
            animate="end"
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </motion.svg>
        );
      
      case 'pulse':
        return (
          <motion.div
            className={`rounded-full bg-current ${sizes[size]} ${colors[color]} ${className}`}
            variants={pulseVariants}
            initial="start"
            animate="end"
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        );
      
      case 'dots':
        return (
          <div className={`flex space-x-1 ${className}`}>
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                className={`rounded-full bg-current ${sizes[size]} ${colors[color]}`}
                variants={bounceVariants}
                initial="start"
                animate="end"
                transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: index * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );
      
      case 'bars':
        return (
          <div className={`flex space-x-1 ${className}`}>
            {[0, 1, 2, 3].map((index) => (
              <motion.div
                key={index}
                className={`bg-current ${colors[color]}`}
                style={{
                  width: size === 'xs' ? '2px' : size === 'sm' ? '3px' : size === 'md' ? '4px' : size === 'lg' ? '5px' : '6px',
                  height: size === 'xs' ? '12px' : size === 'sm' ? '16px' : size === 'md' ? '24px' : size === 'lg' ? '32px' : '40px'
                }}
                animate={{
                  scaleY: [1, 2, 1]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: index * 0.1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );
      
      case 'ring':
        return (
          <motion.div
            className={`border-4 border-gray-200 border-t-current rounded-full ${sizes[size]} ${colors[color]} ${className}`}
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        );
      
      default:
        return null;
    }
  };
  
  const content = (
    <div className="flex flex-col items-center justify-center space-y-3">
      {renderSpinner()}
      {text && (
        <motion.p
          className={`text-sm font-medium ${colors[color]}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
  
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        {content}
      </div>
    );
  }
  
  return content;
};

// Page Loading Component
export const PageLoader = ({ message = "Loading..." }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
    <div className="text-center">
      <LoadingSpinner
        size="xl"
        color="blue"
        type="spinner"
      />
      <motion.h2
        className="mt-4 text-xl font-semibold text-white"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {message}
      </motion.h2>
    </div>
  </div>
);

// Button Loading Component
export const ButtonLoader = ({ size = 'sm', color = 'white' }) => (
  <LoadingSpinner
    size={size}
    color={color}
    type="spinner"
    className="mr-2"
  />
);

// Card Loading Skeleton
export const CardSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-gray-300 h-4 rounded-md mb-2"></div>
    <div className="bg-gray-300 h-4 rounded-md mb-2 w-3/4"></div>
    <div className="bg-gray-300 h-4 rounded-md w-1/2"></div>
  </div>
);

export default LoadingSpinner;
export { LoadingSpinner };